/**
 * /admin/matches/:matchId/videos — per-match ball-video upload + playback.
 *
 * Founder + team use this to drop a tournament's worth of clips, named
 * like "1.1.mp4" / "12.3 wk.mp4", and have each auto-bound to its Ball
 * row. Accepts ZIP archives too — server-extracts.
 *
 * Backend:
 *   GET  /api/admin/v2/matches/<id>/ball-videos/
 *   POST /api/admin/v2/matches/<id>/upload-ball-videos/  multipart files[]
 */
import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Upload, Video, CheckCircle2, AlertCircle, X, Play, FileArchive,
  ThumbsUp, ThumbsDown, ShieldCheck, Trash2, RefreshCw,
} from "lucide-react";
import axios from "axios";
import api from "../../api/axios";
import { purgeMatchVideos } from "../../api/wallet.api";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Raw axios for direct PUT to a presigned S3 URL. We can't use the `api`
// helper here because it injects our Authorization header, which would
// be a "signed header" not included in the presigned signature and S3
// would reject the request as malformed.
const axiosRawPut = (url, body, config) => axios.put(url, body, config);

export default function AdminMatchVideos() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Upload progress for the active POST. `phase` is "uploading" while
  // the multipart bytes stream up to the server, then "processing"
  // after the last byte is acknowledged while the server unpacks the
  // ZIP + binds each clip (can take a couple of minutes on 1+ GB ZIPs).
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  // Cross-check mode: clicking a row opens a side panel with player +
  // ball metadata so the operator can confirm/reject each binding in
  // sequence without re-opening the table between rows.
  const [crossCheckRow, setCrossCheckRow] = useState(null);
  const [busyVideoId, setBusyVideoId] = useState(null);
  const [filterInn, setFilterInn] = useState(0); // 0 = all
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [showUnverifiedOnly, setShowUnverifiedOnly] = useState(false);
  // 2026-08-04 — archive state of this match's full-match master
  // (masters live in Glacier Deep Archive; founder ask: surface the
  // Retrieve button right next to Video tagging so the operator can
  // start the ≤48 h restore a couple of days before a tagging session).
  // null = no master uploaded; else {state, restore_expiry}.
  const [masterArchive, setMasterArchive] = useState(null);
  const [retrieveBusy, setRetrieveBusy] = useState(false);
  const fileInput = useRef(null);
  const videoPanelRef = useRef(null);
  useDialogDismiss({
    open: !!playingUrl,
    onClose: () => setPlayingUrl(null),
    ref: videoPanelRef,
  });
  useDialogDismiss({
    open: !!crossCheckRow,
    onClose: () => setCrossCheckRow(null),
    ref: videoPanelRef,
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/v2/matches/${matchId}/ball-videos/`);
      setData(r.data);
    } catch (e) {
      console.error(e);
      setData({ rows: [], error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [matchId]);

  const fetchMasterArchive = async () => {
    try {
      const r = await api.get(`/api/admin/v2/matches/${matchId}/whole-match-video/`);
      const m = r.data?.master;
      setMasterArchive(m ? {
        state: m.video_state || "available",
        restore_expiry: m.restore_expiry || null,
      } : null);
    } catch {
      setMasterArchive(null);   // status is advisory — never block the page
    }
  };
  useEffect(() => { fetchMasterArchive(); /* eslint-disable-next-line */ }, [matchId]);

  const retrieveMaster = async () => {
    setRetrieveBusy(true);
    try {
      const r = await api.post(`/api/admin/v2/matches/${matchId}/retrieve-whole-match-video/`);
      setMasterArchive({
        state: r.data?.state || "retrieving",
        restore_expiry: r.data?.restore_expiry || null,
      });
    } catch (e) {
      alert(`Retrieve failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRetrieveBusy(false);
    }
  };

  // innings: 1 | 2 | null  — null = filename-detected (kept for single-file uploads)
  // convention: "vendor" | "scorecard" | "auto"  — defaults to "auto" for
  // single-file uploads, "vendor" for the per-innings dropzones.
  //
  // ZIP uploads go via the presigned-S3 + async-bind flow:
  //   1. POST /presign-upload/        -> tiny call, get presigned PUT URL
  //   2. PUT  bytes to S3 directly    -> bypasses nginx/gunicorn entirely
  //   3. POST /bind-job/<job_id>/     -> spawns background binding, returns 202
  //   4. GET  /upload-jobs/<job_id>/  -> poll until status == complete | failed
  //
  // Single-file (non-ZIP) uploads keep the legacy /upload-ball-videos/
  // multipart POST — small files don't risk the 60s middlebox cutoff.
  const handleFiles = async (files, { innings = null, convention = "auto" } = {}) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadResult(null);
    try {
      // Split ZIPs (presigned path) vs everything else (legacy path).
      const arr = Array.from(files);
      const zips = arr.filter((f) => /\.zip$/i.test(f.name));
      const others = arr.filter((f) => !/\.zip$/i.test(f.name));

      const merged = {
        created: 0, replaced: 0, unmatched: [], errors: [],
        total_videos_processed: 0, _innings: innings,
      };

      // ── Presigned path for each ZIP ─────────────────────────────────
      for (const zip of zips) {
        const totalBytes = zip.size || 0;
        setUploadProgress({
          loaded: 0, total: totalBytes, pct: 0,
          phase: "uploading", innings,
          filenames: [zip.name], fileCount: 1, startedAt: Date.now(),
        });

        // 1) Get presigned URL
        const pre = await api.post(
          `/api/admin/v2/matches/${matchId}/presign-upload/`,
          { filename: zip.name },
        );
        const { job_id, presigned_url, required_headers } = pre.data;

        // 2) PUT bytes directly to S3 (no auth header — signature in URL)
        await axiosRawPut(presigned_url, zip, {
          headers: { ...(required_headers || {}), "Content-Type": "application/zip" },
          onUploadProgress: (ev) => {
            const total = ev.total || totalBytes || 0;
            const loaded = ev.loaded || 0;
            const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
            setUploadProgress((p) => p && {
              ...p, loaded, total, pct,
              phase: pct >= 100 ? "processing" : "uploading",
            });
          },
        });

        // 3) Start the bind job
        await api.post(
          `/api/admin/v2/matches/${matchId}/bind-job/${job_id}/`,
          { innings: innings ?? undefined, convention },
        );

        // 4) Poll for completion (every 3s, up to 30 min). Each poll
        // surfaces server-side progress (download phase, then binding
        // phase) so the bar continues to move after the PUT is done.
        const startedAt = Date.now();
        let finalJob = null;
        while (Date.now() - startedAt < 30 * 60 * 1000) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const s = await api.get(`/api/admin/v2/upload-jobs/${job_id}/`);
            const j = s.data;
            // Render the binding/downloading phase onto the same
            // progress bar that drove the upload phase.
            const p = j.progress;
            if (p) {
              setUploadProgress((prev) => prev && {
                ...prev,
                // Reuse loaded/total semantics: in the binding phase,
                // loaded = clips bound, total = clips in ZIP.
                loaded: p.done || 0,
                total: p.total || 0,
                pct: p.pct || 0,
                phase: p.phase || "processing",
                // Carry result counters so the dropzone label can show
                // "32 of 127 clips bound" while it's ticking.
                serverPhase: p.phase,
                serverDone: p.done,
                serverTotal: p.total,
              });
            }
            if (j.status === "complete" || j.status === "failed") {
              finalJob = j; break;
            }
          } catch (e) {
            // Ignore transient poll errors; keep trying.
          }
        }
        if (!finalJob) throw new Error(`Bind timed out for ${zip.name}`);
        if (finalJob.status === "failed") {
          throw new Error(`Bind failed: ${finalJob.error || "unknown"}`);
        }
        const r = finalJob.result || {};
        merged.created += r.created || 0;
        merged.replaced += r.replaced || 0;
        merged.unmatched = merged.unmatched.concat(r.unmatched || []);
        merged.errors = merged.errors.concat(r.errors || []);
        merged.total_videos_processed += r.total_videos_processed || 0;
      }

      // ── Legacy multipart path for any non-ZIP single files ──────────
      if (others.length) {
        const totalBytes = others.reduce((s, f) => s + (f.size || 0), 0);
        setUploadProgress({
          loaded: 0, total: totalBytes, pct: 0,
          phase: "uploading", innings,
          filenames: others.map((f) => f.name).slice(0, 5),
          fileCount: others.length, startedAt: Date.now(),
        });
        const fd = new FormData();
        for (const f of others) fd.append("files", f, f.name);
        if (innings === 1 || innings === 2) fd.append("innings", String(innings));
        if (convention) fd.append("convention", convention);
        const r = await api.post(
          `/api/admin/v2/matches/${matchId}/upload-ball-videos/`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30 * 60 * 1000,
            onUploadProgress: (ev) => {
              const total = ev.total || totalBytes || 0;
              const loaded = ev.loaded || 0;
              const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
              setUploadProgress((p) => p && {
                ...p, loaded, total, pct,
                phase: pct >= 100 ? "processing" : "uploading",
              });
            },
          },
        );
        merged.created += r.data.created || 0;
        merged.replaced += r.data.replaced || 0;
        merged.unmatched = merged.unmatched.concat(r.data.unmatched || []);
        merged.errors = merged.errors.concat(r.data.errors || []);
        merged.total_videos_processed += r.data.total_videos_processed || 0;
      }

      setUploadResult(merged);
      await fetchRows();
    } catch (e) {
      setUploadResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const rows = (data?.rows || []).filter((r) => {
    if (filterInn && r.innings !== filterInn) return false;
    if (showMissingOnly && r.has_video) return false;
    if (showUnverifiedOnly && r.video_state === "verified") return false;
    return true;
  });

  // Per-row "Upload clip" — pinned to a specific (innings, over, ball)
  // so the operator can fill the gap left by an unmatched ZIP entry
  // without re-uploading the whole ZIP. We rewrite the filename to the
  // scorecard convention (over.ball.mp4) so _find_ball lands on this
  // exact Ball row, and force convention=scorecard so the backend
  // doesn't apply the vendor +1-over remap on our synthesized name.
  const [missingRowBusyId, setMissingRowBusyId] = useState(null);
  async function uploadClipForBall(file, row) {
    if (!file) return;
    setMissingRowBusyId(row.ball_id);
    const ext = (file.name.match(/\.[^./\\]+$/)?.[0] || ".mp4").toLowerCase();
    const renamed = new File(
      [file], `${row.over}.${row.ball}${ext}`,
      { type: file.type || "video/mp4" },
    );
    const fd = new FormData();
    fd.append("files", renamed, renamed.name);
    fd.append("innings", String(row.innings));
    fd.append("convention", "scorecard");
    try {
      await api.post(
        `/api/admin/v2/matches/${matchId}/upload-ball-videos/`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
        },
      );
      await fetchRows();
    } catch (e) {
      alert(
        `Upload for ${row.innings === 2 ? "i2" : "i1"} ${row.over}.${row.ball} failed: ` +
        (e?.response?.data?.detail || e.message),
      );
    } finally {
      setMissingRowBusyId(null);
    }
  }

  const total = data?.total_balls || 0;
  const linked = data?.videos_linked || 0;
  const verified = data?.videos_verified || 0;
  const pct = total ? Math.round((linked / total) * 100) : 0;

  // 2026-06-09 — Bulk delete of every BallVideo + MatchRecording for
  // this match. Wired to the same backend endpoint StorageManager uses
  // (POST /api/matches/<id>/purge-videos/) which keeps scorecard +
  // ball-by-ball + analytics untouched and only nukes the heavy video
  // assets. Used when the operator binds the wrong ZIP and wants a
  // clean slate before re-uploading.
  const [purging, setPurging] = useState(false);
  async function handlePurgeAllVideos() {
    if (purging || linked === 0) return;
    if (!confirm(
      `Delete EVERY video clip for this match (${linked} clips${verified ? `, ${verified} verified` : ""})?\n\n` +
      `The scorecard, ball-by-ball data and all stats are KEPT — only the ` +
      `video assets are removed. Use this when the wrong ZIP was uploaded ` +
      `and you want a clean slate.\n\n` +
      `This cannot be undone.`
    )) return;
    setPurging(true);
    try {
      const res = await purgeMatchVideos(matchId);
      const v = res.data?.videos_deleted ?? 0;
      const r = res.data?.recordings_deleted ?? 0;
      alert(`Removed ${v} clip${v === 1 ? "" : "s"}${r ? ` and ${r} master recording${r === 1 ? "" : "s"}` : ""}.`);
      // Reload the row table so the now-empty state renders.
      await fetchRows();
    } catch (e) {
      alert(
        `Purge failed: ${e?.response?.data?.detail || e?.message || "unknown error"}`
      );
    } finally {
      setPurging(false);
    }
  }

  // Cross-check actions wired to the new admin endpoints.
  async function confirmRow(row) {
    if (!row.video_id) return;
    setBusyVideoId(row.video_id);
    try {
      await api.post(
        `/api/admin/v2/matches/${matchId}/ball-videos/${row.video_id}/confirm/`,
      );
      setData((d) => d && {
        ...d,
        videos_verified: (d.videos_verified || 0) + 1,
        rows: d.rows.map((r) =>
          r.video_id === row.video_id ? { ...r, video_state: "verified" } : r
        ),
      });
      // Auto-advance to the next un-verified row so the operator can
      // sit on the keyboard / mouse and burn through the queue.
      const idx = rows.findIndex((r) => r.ball_id === row.ball_id);
      const next = rows.slice(idx + 1).find(
        (r) => r.has_video && r.video_state !== "verified",
      );
      setCrossCheckRow(next || null);
    } catch (e) {
      alert(`Confirm failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyVideoId(null);
    }
  }

  async function deleteRow(row) {
    if (!row.video_id) return;
    const label = `i${row.innings} · ${Math.max(0, (row.over || 1) - 1)}.${row.ball}`;
    if (!confirm(
      `Delete the video clip for ball ${label}?\n\n` +
      `The clip file will be removed from S3 and the row will reset to 'no video'. ` +
      `Scorecard + ball data are kept. You can re-upload via the upload panel.\n\n` +
      `This cannot be undone.`,
    )) return;
    setBusyVideoId(row.video_id);
    try {
      await api.post(
        `/api/admin/v2/matches/${matchId}/ball-videos/${row.video_id}/reject/`,
      );
      setData((d) => d && {
        ...d,
        videos_linked: (d.videos_linked || 0) - 1,
        videos_verified: (d.videos_verified || 0) - (row.video_state === "verified" ? 1 : 0),
        rows: d.rows.map((r) =>
          r.ball_id === row.ball_id
            ? { ...r, video_id: null, has_video: false, video_url: null, video_state: null }
            : r
        ),
      });
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyVideoId(null);
    }
  }

  async function rejectRow(row) {
    if (!row.video_id) return;
    if (!confirm(
      `Reject the clip currently bound to ${row.innings === 2 ? "2." : "1."}${(row.over || 1) - 1}.${row.ball}? ` +
      `The clip will be deleted from S3 and the row will go back to 'no video' state. ` +
      `You can re-upload manually via the upload panel.`,
    )) return;
    setBusyVideoId(row.video_id);
    try {
      await api.post(
        `/api/admin/v2/matches/${matchId}/ball-videos/${row.video_id}/reject/`,
      );
      setData((d) => d && {
        ...d,
        videos_linked: (d.videos_linked || 0) - 1,
        videos_verified: (d.videos_verified || 0) - (row.video_state === "verified" ? 1 : 0),
        rows: d.rows.map((r) =>
          r.ball_id === row.ball_id
            ? { ...r, video_id: null, has_video: false, video_url: null, video_state: null }
            : r
        ),
      });
      const idx = rows.findIndex((r) => r.ball_id === row.ball_id);
      const next = rows.slice(idx + 1).find(
        (r) => r.has_video && r.video_state !== "verified",
      );
      setCrossCheckRow(next || null);
    } catch (e) {
      alert(`Reject failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyVideoId(null);
    }
  }

  async function verifyAll() {
    const readyCount = rows.filter(
      (r) => r.has_video && r.video_state !== "verified",
    ).length;
    if (!readyCount) return;
    if (!confirm(
      `Mark all ${readyCount} unverified clips on this match as verified? ` +
      `This is a one-click trust action — only do it after spot-checking a few rows by hand.`,
    )) return;
    try {
      const r = await api.post(
        `/api/admin/v2/matches/${matchId}/ball-videos/bulk-verify/`,
      );
      const n = r.data?.updated || 0;
      await fetchRows();
      alert(`Marked ${n} clip(s) as verified.`);
    } catch (e) {
      alert(`Verify-all failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-text-secondary hover:text-text-primary"
          >
            ← back
          </button>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl font-semibold">Ball videos</h1>
            <Link
              to={`/admin/matches/${matchId}/video-tagging`}
              className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 inline-flex items-center gap-1"
              title="Manually tag clip IN/OUT per ball from a full-match MP4"
            >
              🎞 Video tagging
            </Link>
            {/* 2026-08-04 — masters sit in Glacier Deep Archive; the
                operator clicks Retrieve ~2 days before a tagging
                session so the video is playable when they sit down. */}
            {masterArchive?.state === "archived" && (
              <button
                onClick={retrieveMaster}
                disabled={retrieveBusy}
                className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300 hover:bg-sky-500/25 disabled:opacity-40 inline-flex items-center gap-1"
                title="The full-match video is in Glacier Deep Archive. Start the retrieval now — it becomes taggable within 48 hours and stays available for 14 days."
              >
                ⏳ {retrieveBusy ? "Requesting…" : "Retrieve video for tagging (≤48 h)"}
              </button>
            )}
            {masterArchive?.state === "retrieving" && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 inline-flex items-center gap-1"
                    title="Retrieval in progress — the video becomes taggable within 48 hours of the request (12 h on expedited restores).">
                ⏳ Retrieving video… ready ≤48 h
              </span>
            )}
            {masterArchive?.state === "restored" && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-1"
                    title="Retrieved from the archive — playback and tagging work normally until the date shown, then it re-archives automatically.">
                ✓ Video ready
                {masterArchive.restore_expiry ? ` until ${new Date(masterArchive.restore_expiry).toLocaleDateString()}` : ""}
              </span>
            )}
          </div>
          {data?.team1 && (() => {
            // Match-number resolution: prefer the dataset's identifier
            // (Match.ci_id — Cricsheet's event.match_number or external id).
            // Fall back to the first 8 hex chars of Match.id so something
            // distinctive always renders — critical when two matches
            // between the same teams need to be told apart at a glance.
            const datasetNo = data.match_number && String(data.match_number).trim();
            const shortId = data.match_id ? String(data.match_id).slice(0, 8) : "";
            const matchLabel = datasetNo || shortId;
            return (
              <p className="mt-1 flex flex-wrap items-center gap-2 text-base">
                {matchLabel && (
                  <span
                    title={
                      datasetNo
                        ? "Match number from the source dataset (vendor match_no). Confirm you're on the right fixture before uploading clips."
                        : "Internal match ID (first 8 chars). Used because this match has no external dataset number."
                    }
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/15 border border-sky-400/40 text-sky-200 font-mono font-semibold"
                  >
                    {datasetNo ? `Match ${matchLabel}` : `Match #${matchLabel}`}
                  </span>
                )}
                <span className="text-text-primary font-medium">
                  {data.team1} vs {data.team2}
                </span>
              </p>
            );
          })()}
          {(data?.competition || data?.series || data?.match_date) && (
            <p className="mt-0.5 text-xs text-text-secondary flex flex-wrap items-center gap-x-2">
              {(data.competition || data.series) && (
                <span>{data.competition || data.series}</span>
              )}
              {data.match_date && (
                <span>· {data.match_date}</span>
              )}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-3xl font-semibold">{linked}/{total}</div>
          <div className="text-xs text-text-secondary">{pct}% balls have video</div>
          {linked > 0 && (
            <div className="text-[11px] text-emerald-300 mt-0.5">
              <ShieldCheck className="w-3 h-3 inline-block mr-0.5" />
              {verified} of {linked} verified
            </div>
          )}
          {linked > 0 && (
            <button
              type="button"
              onClick={handlePurgeAllVideos}
              disabled={purging}
              title="Delete every video clip + master recording for this match. Scorecard + ball-by-ball data are kept; only the heavy video assets are removed."
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] uppercase tracking-wider bg-rose-500/15 border border-rose-400/40 text-rose-300 hover:bg-rose-500/25 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" />
              {purging ? "Deleting…" : "Delete all videos"}
            </button>
          )}
        </div>
      </div>

      {/* ── Upload panel ─────────────────────────────────────────────── */}
      <div className="border border-border-default rounded-lg p-5 bg-bg-secondary/30">
        <div>
          <h2 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload videos
          </h2>
          <p className="text-xs text-text-secondary mb-4">
            Drop the vendor ZIP for each innings into the matching dropzone below.
            Every clip is pinned to that innings on this match and is treated as
            vendor convention: <code className="bg-bg-tertiary px-1 rounded">1.1.mp4</code> means{" "}
            "1st ball after 1 over complete" = ball 1 of over 2; the very first ball is{" "}
            <code className="bg-bg-tertiary px-1 rounded">0.1.mp4</code>. Switch the toggle to
            "Scorecard" if your ZIP names <code className="bg-bg-tertiary px-1 rounded">1.1</code>{" "}
            as the very first ball. Max 1.5 GB / ZIP.
          </p>

          {/* Per-innings dropzones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {[1, 2].map((inn) => (
              <InningsDropzone
                key={inn}
                innings={inn}
                disabled={uploading}
                activeProgress={uploadProgress && uploadProgress.innings === inn ? uploadProgress : null}
                onFiles={(files, opts) => handleFiles(files, { innings: inn, ...opts })}
              />
            ))}
          </div>

          {/* Global upload progress (also shown when the upload is the
              single-file advanced path that doesn't belong to a dropzone). */}
          {uploadProgress && (
            <UploadProgressBar progress={uploadProgress} />
          )}

          {/* Whole-match upload + alignment (Phase 1: math aligner;
              Phase 2: OCR re-align). */}
          <WholeMatchUploadPanel
            matchId={matchId}
            disabled={uploading}
            onAfterApply={fetchRows}
            setUploading={setUploading}
            setUploadProgress={setUploadProgress}
          />

          {/* Single-file fallback */}
          <details className="text-xs">
            <summary className="cursor-pointer text-text-secondary hover:text-text-primary">
              Single files / operator-named uploads (advanced)
            </summary>
            <div className="mt-2 p-3 border border-border-default rounded bg-bg-tertiary/30">
              <p className="text-xs text-text-secondary mb-2">
                For one-off clips named in scorecard convention:{" "}
                <code className="bg-bg-tertiary px-1 rounded">1.1.mp4</code> = over 1 ball 1,{" "}
                <code className="bg-bg-tertiary px-1 rounded">i2_3.4.mp4</code> = innings 2 over 3 ball 4.
                Each file's innings is parsed from the filename.
              </p>
              <button
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="bg-button-primary hover:brightness-110 disabled:opacity-50 px-3 py-1.5 rounded text-sm font-semibold text-white inline-flex items-center gap-1.5"
              >
                <Upload className="w-3.5 h-3.5" />
                {uploading ? "Uploading…" : "Choose files"}
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept="*/*"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
              />
            </div>
          </details>
        </div>

        {uploadResult && (
          <div className="mt-4 border-t border-border-default pt-4 text-sm">
            {uploadResult.error ? (
              <div className="text-red-300 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" /> {uploadResult.error}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-xs">
                  {uploadResult._innings && (
                    <span className="px-1.5 py-0.5 rounded bg-bg-tertiary text-text-secondary">
                      Innings {uploadResult._innings}
                    </span>
                  )}
                  <span className="text-emerald-300">
                    ✓ {uploadResult.created} created
                  </span>
                  {uploadResult.replaced > 0 && (
                    <span className="text-blue-300">
                      ↻ {uploadResult.replaced} replaced
                    </span>
                  )}
                  {uploadResult.unmatched?.length > 0 && (
                    <span className="text-amber-300">
                      ? {uploadResult.unmatched.length} unmatched
                    </span>
                  )}
                  {uploadResult.errors?.length > 0 && (
                    <span className="text-red-300">
                      ✗ {uploadResult.errors.length} errors
                    </span>
                  )}
                  <span className="text-text-secondary">
                    ({uploadResult.total_videos_processed} videos seen)
                  </span>
                </div>
                {uploadResult.unmatched?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-amber-300">
                      Unmatched files ({uploadResult.unmatched.length})
                    </summary>
                    <ul className="mt-1 ml-4 list-disc text-text-secondary">
                      {uploadResult.unmatched.slice(0, 50).map((u, i) => (
                        <li key={i}>
                          <span className="font-mono">{u.file}</span> — {u.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {uploadResult.errors?.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-red-300">
                      Errors ({uploadResult.errors.length})
                    </summary>
                    <ul className="mt-1 ml-4 list-disc text-text-secondary">
                      {uploadResult.errors.slice(0, 50).map((e, i) => (
                        <li key={i}>
                          <span className="font-mono">{e.file}</span> — {e.detail}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-text-secondary">Filter:</span>
        {[0, 1, 2].map((n) => (
          <button
            key={n}
            onClick={() => setFilterInn(n)}
            className={`px-2 py-1 rounded border ${
              filterInn === n
                ? "bg-bg-tertiary border-border-active"
                : "border-border-default text-text-secondary hover:text-text-primary"
            }`}
          >
            {n === 0 ? "All innings" : `Innings ${n}`}
          </button>
        ))}
        <label className="flex items-center gap-1.5 ml-3 cursor-pointer">
          <input
            type="checkbox"
            checked={showMissingOnly}
            onChange={(e) => setShowMissingOnly(e.target.checked)}
            className="rounded"
          />
          missing video only
        </label>
        <label className="flex items-center gap-1.5 ml-3 cursor-pointer">
          <input
            type="checkbox"
            checked={showUnverifiedOnly}
            onChange={(e) => setShowUnverifiedOnly(e.target.checked)}
            className="rounded"
          />
          unverified only
        </label>
        {linked > 0 && (
          <button
            onClick={verifyAll}
            className="ml-3 text-xs px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 inline-flex items-center gap-1"
            title="Spot-check, then one-click trust the remaining rows"
          >
            <ShieldCheck className="w-3 h-3" /> Verify all
          </button>
        )}
        <span className="ml-auto text-text-secondary">{rows.length} rows</span>
      </div>

      {/* ── Ball table ─────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-text-secondary">Loading balls…</div>
      ) : (
        <div className="overflow-x-auto border border-border-default rounded">
          <table className="w-full text-xs">
            <thead className="bg-bg-secondary/50 text-text-secondary">
              <tr>
                <th className="text-left p-2">Inn</th>
                <th className="text-left p-2">Over.Ball</th>
                <th className="text-left p-2">Striker</th>
                <th className="text-left p-2">Bowler</th>
                <th className="text-right p-2">Runs</th>
                <th className="text-left p-2">Wkt</th>
                <th className="text-left p-2">Video</th>
                <th className="text-left p-2">Cross-check</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ball_id} className="border-t border-border-default hover:bg-bg-secondary/30">
                  <td className="p-2">{r.innings}</td>
                  {/* Display in the live-BBP convention:
                      "(completed overs).(balls bowled in current over)".
                      DB stores over_number 1-based (cricket scorecard
                      convention), so the first ball is over=1 ball=1 in
                      the row — render as 0.1 to match the counter the
                      operator saw on the scoring page.
                      Upload filenames remain 1-based per the help text
                      above and the _parse_filename helper. */}
                  <td className="p-2 font-mono">{Math.max(0, (r.over || 1) - 1)}.{r.ball}</td>
                  <td className="p-2">{r.striker}</td>
                  <td className="p-2">{r.bowler}</td>
                  <td className="p-2 text-right">
                    {r.runs_off_bat}
                    {r.extras > 0 && <span className="text-text-secondary"> +{r.extras}</span>}
                    {r.is_boundary && <span className="ml-1 text-amber-300">★</span>}
                  </td>
                  <td className="p-2">
                    {r.wicket_type && (
                      <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px]">
                        {r.wicket_type}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    {r.has_video ? (
                      <button
                        onClick={() => setCrossCheckRow(r)}
                        className={`flex items-center gap-1 hover:underline ${
                          r.video_state === "verified"
                            ? "text-emerald-300"
                            : "text-blue-300"
                        }`}
                        title={
                          r.video_state === "verified"
                            ? "Verified — click to re-open in cross-check panel"
                            : "Open in cross-check panel"
                        }
                      >
                        {r.video_state === "verified"
                          ? <ShieldCheck className="w-3 h-3" />
                          : <Play className="w-3 h-3" />
                        }
                        {r.video_state === "verified" ? "verified" : "play"}
                      </button>
                    ) : (
                      <span className="text-text-secondary text-[10px]">—</span>
                    )}
                  </td>
                  <td className="p-2">
                    {r.has_video ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => confirmRow(r)}
                          disabled={busyVideoId === r.video_id || r.video_state === "verified"}
                          className="text-emerald-300 hover:text-emerald-100 disabled:opacity-30 p-0.5 inline-flex items-center gap-0.5 text-[10px] border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5"
                          title="Confirm — clip matches this delivery"
                        >
                          <ThumbsUp className="w-3 h-3" />
                          OK
                        </button>
                        <button
                          onClick={() => rejectRow(r)}
                          disabled={busyVideoId === r.video_id}
                          className="text-rose-300 hover:text-rose-100 disabled:opacity-30 p-0.5 inline-flex items-center gap-0.5 text-[10px] border border-rose-500/40 bg-rose-500/10 rounded px-1.5"
                          title="Reject — clip doesn't match; delete it so you can re-upload"
                        >
                          <ThumbsDown className="w-3 h-3" />
                          Wrong
                        </button>
                        <button
                          onClick={() => deleteRow(r)}
                          disabled={busyVideoId === r.video_id}
                          className="text-rose-300 hover:text-rose-100 disabled:opacity-30 p-0.5 inline-flex items-center gap-0.5 text-[10px] border border-rose-500/40 bg-rose-500/15 rounded px-1.5"
                          title="Delete this clip — removes the file from S3 (scorecard kept)"
                        >
                          <Trash2 className="w-3 h-3" />
                          Del
                        </button>
                      </div>
                    ) : (
                      <MissingClipUploader
                        row={r}
                        busy={missingRowBusyId === r.ball_id}
                        onPick={(file) => uploadClipForBall(file, r)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Cross-check side-by-side panel ─────────────────────────── */}
      {crossCheckRow && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setCrossCheckRow(null)}
        >
          <div
            ref={videoPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Cross-check ball video"
            className="bg-bg-primary border border-border-default rounded-lg p-4 max-w-5xl w-full grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Video pane */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <span className="font-mono">
                  i{crossCheckRow.innings} · {Math.max(0, (crossCheckRow.over || 1) - 1)}.{crossCheckRow.ball}
                </span>
                {crossCheckRow.video_state === "verified" && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[10px] inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> verified
                  </span>
                )}
              </div>
              <video
                key={crossCheckRow.video_id}
                src={crossCheckRow.video_url}
                controls
                autoPlay
                className="w-full max-h-[70vh] bg-black rounded"
              />
            </div>
            {/* Metadata + actions pane */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-end">
                <button
                  onClick={() => setCrossCheckRow(null)}
                  className="text-text-secondary hover:text-text-primary"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="rounded-md border border-border-default p-3 space-y-1">
                <div className="text-[11px] uppercase tracking-wider text-text-secondary">
                  Expected ball
                </div>
                <div className="font-mono text-base">
                  innings {crossCheckRow.innings} · over {crossCheckRow.over}, ball {crossCheckRow.ball}
                </div>
                <div className="text-xs text-text-secondary">
                  (SPA label: {Math.max(0, (crossCheckRow.over || 1) - 1)}.{crossCheckRow.ball})
                </div>
              </div>
              <div className="rounded-md border border-border-default p-3 space-y-1.5">
                <div className="text-[11px] uppercase tracking-wider text-text-secondary">
                  Scorecard
                </div>
                <div><span className="text-text-secondary">Striker:</span> {crossCheckRow.striker || "—"}</div>
                <div><span className="text-text-secondary">Bowler:</span> {crossCheckRow.bowler || "—"}</div>
                <div>
                  <span className="text-text-secondary">Runs:</span>{" "}
                  {crossCheckRow.runs_off_bat}
                  {crossCheckRow.extras > 0 && (
                    <span className="text-text-secondary"> +{crossCheckRow.extras} extras</span>
                  )}
                  {crossCheckRow.is_boundary && (
                    <span className="ml-1 text-amber-300">★ boundary</span>
                  )}
                </div>
                {crossCheckRow.wicket_type && (
                  <div className="text-red-300">Wicket: {crossCheckRow.wicket_type}</div>
                )}
                {!crossCheckRow.is_legal && (
                  <div className="text-amber-300">Extra delivery (not legal)</div>
                )}
              </div>
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-emerald-200">
                  Does the clip match?
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => confirmRow(crossCheckRow)}
                    disabled={busyVideoId === crossCheckRow.video_id || crossCheckRow.video_state === "verified"}
                    className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 text-sm font-semibold"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Confirm
                  </button>
                  <button
                    onClick={() => rejectRow(crossCheckRow)}
                    disabled={busyVideoId === crossCheckRow.video_id}
                    className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 disabled:opacity-40 text-sm font-semibold"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Wrong
                  </button>
                </div>
                <div className="text-[10px] text-text-secondary">
                  Confirm advances to the next un-verified row. Reject deletes the clip from S3.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Legacy: pre-cross-check video modal kept for callers that pass a
          bare URL. New flow always uses the side-by-side panel above. */}
      {playingUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setPlayingUrl(null)}
        >
          <div
            ref={videoPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Ball video"
            className="bg-bg-primary border border-border-default rounded-lg p-3 max-w-3xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end mb-2">
              <button onClick={() => setPlayingUrl(null)}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <video src={playingUrl} controls autoPlay className="w-full max-h-[70vh]" />
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Per-innings ZIP dropzone. Drag+drop or click-to-pick — every file
 * is sent with `innings=<n>` + `convention` so the backend pins the
 * binding to the right innings AND interprets filenames the same way
 * the vendor named them. Matches the pattern used on the Ingest
 * Quick Upload panel.
 */
/** Human-friendly byte formatter (1.2 GB / 845 MB / 12 KB). */
function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

/**
 * Detailed progress bar shown beneath the dropzones during an upload.
 * Phase = "uploading" -> show bytes streamed + ETA. Phase = "processing"
 * -> bar pinned at 100% with a 'server is unpacking' note, since the
 * actual ZIP unpack + per-clip S3 upload can take several minutes.
 */
function UploadProgressBar({ progress }) {
  const {
    pct, loaded, total, phase, innings, fileCount, filenames, startedAt,
    serverPhase, serverDone, serverTotal,
  } = progress;
  const elapsed = (Date.now() - (startedAt || Date.now())) / 1000;
  const speed = phase === "uploading" && elapsed > 0 ? loaded / elapsed : 0;  // bytes/sec
  const remaining = phase === "uploading" && speed > 0 && total > loaded
    ? Math.round((total - loaded) / speed)
    : null;

  // Map the phase token into a human label.
  const phaseLabel = {
    uploading: "Uploading to S3",
    processing: "Processing on server…",
    downloading: "Server downloading ZIP from S3",
    binding: "Mapping clips to balls",
    done: "Finishing up",
  }[serverPhase || phase] || "Working…";

  // Bar color: green during browser->S3 PUT, amber while the server
  // works.
  const isServerPhase = phase !== "uploading";
  const barColor = isServerPhase ? "bg-amber-400" : "bg-emerald-400";

  // In server phases, loaded/total are counts (clips bound / clips in
  // ZIP) rather than bytes — relabel.
  const showCounts = serverPhase === "binding" || serverPhase === "done";

  return (
    <div className="mt-2 p-3 border border-emerald-500/30 bg-emerald-500/5 rounded">
      <div className="flex items-center justify-between text-xs mb-1">
        <div className="flex items-center gap-2">
          {innings && (
            <span className="px-1.5 py-0.5 rounded bg-bg-tertiary text-text-secondary">
              Innings {innings}
            </span>
          )}
          <span className="font-semibold">{phaseLabel}</span>
          {!isServerPhase && (
            <span className="text-text-secondary">
              {fileCount} file{fileCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="font-mono text-sm font-semibold">{pct}%</div>
      </div>
      <div className="w-full h-2 bg-bg-tertiary rounded overflow-hidden">
        <div
          className={`h-full transition-all ${barColor} ${
            isServerPhase ? "animate-pulse" : ""
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1 text-[11px] text-text-secondary font-mono">
        <span>
          {showCounts
            ? `${serverDone || 0} of ${serverTotal || 0} clips bound`
            : <>
                {fmtBytes(loaded)} / {fmtBytes(total)}
                {phase === "uploading" && speed > 0 && (
                  <span className="ml-2">{fmtBytes(speed)}/s</span>
                )}
              </>
          }
        </span>
        <span>
          {phase === "uploading" && remaining !== null
            ? `~${remaining}s remaining`
            : serverPhase === "downloading"
            ? "Streaming ZIP from S3 into the worker"
            : serverPhase === "binding"
            ? "OK to leave this tab — server keeps going"
            : ""}
        </span>
      </div>
      {filenames?.length > 0 && (
        <div className="mt-1 text-[10px] text-text-secondary truncate">
          {filenames.join(", ")}
          {fileCount > filenames.length && ` (+${fileCount - filenames.length} more)`}
        </div>
      )}
    </div>
  );
}

function InningsDropzone({ innings, disabled, onFiles, activeProgress }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  // Vendor live-BBP is the realistic default — the founder's actual
  // workflow has been "drop a vendor ZIP per innings" since day one.
  // Auto-detect kept failing for ZIPs that don't emit X.0 / 0.X
  // marker clips (Maharaja match 2, 2026-06-08), so the explicit
  // toggle is the safer surface.
  const [convention, setConvention] = useState("vendor");

  const submit = (files) => onFiles(files, { convention });

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (disabled) return;
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length) submit(files);
      }}
      className={[
        "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
        disabled ? "opacity-50" : "",
        drag ? "border-emerald-400 bg-emerald-500/10" : "border-border-default",
      ].join(" ")}
    >
      <div
        onClick={() => !disabled && inputRef.current?.click()}
        className={`cursor-pointer ${disabled ? "cursor-not-allowed" : "hover:opacity-90"}`}
      >
        <FileArchive className="w-6 h-6 mx-auto mb-1 text-text-secondary" />
        <div className="text-sm font-semibold">
          Innings {innings} ZIP
          {activeProgress && (
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-mono text-emerald-300">
              {activeProgress.phase === "processing" ? "processing…" : `${activeProgress.pct}%`}
            </span>
          )}
        </div>
        <div className="text-[11px] text-text-secondary">
          {activeProgress
            ? activeProgress.phase === "processing"
              ? "Server is unpacking + binding clips"
              : `Sending — ${fmtBytes(activeProgress.loaded)} of ${fmtBytes(activeProgress.total)}`
            : `Drop or click — every clip pinned to innings ${innings}`}
        </div>
      </div>

      {/* Convention toggle */}
      <div
        className="mt-3 flex items-center justify-center gap-2 text-[11px]"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-text-secondary">Naming:</span>
        {[
          { v: "vendor",    label: "Vendor (1.1 = over 2 ball 1)" },
          { v: "scorecard", label: "Scorecard (1.1 = over 1 ball 1)" },
        ].map((opt) => (
          <label
            key={opt.v}
            className={[
              "cursor-pointer px-2 py-0.5 rounded border",
              convention === opt.v
                ? "bg-bg-tertiary border-border-active text-text-primary"
                : "border-border-default text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            <input
              type="radio"
              name={`conv-${innings}`}
              checked={convention === opt.v}
              onChange={() => setConvention(opt.v)}
              className="hidden"
            />
            {opt.label}
          </label>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="*/*"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) submit(files);
          e.target.value = "";
        }}
        className="hidden"
      />
    </div>
  );
}


/** Parse "mm:ss" or "hh:mm:ss" to milliseconds. Returns NaN on bad input. */
function parseHmsToMs(s) {
  if (!s) return NaN;
  const parts = String(s).trim().split(":").map((x) => x.trim());
  if (parts.some((p) => p === "" || !/^\d+(\.\d+)?$/.test(p))) return NaN;
  let h = 0, m = 0, sec = 0;
  if (parts.length === 2) { [m, sec] = parts.map(parseFloat); }
  else if (parts.length === 3) { [h, m, sec] = parts.map(parseFloat); }
  else return NaN;
  return Math.round((h * 3600 + m * 60 + sec) * 1000);
}

/**
 * Phase 1: upload one full-match MP4/MOV via presigned PUT, then call
 * the math aligner (existing /align-full-video/ endpoint) with the
 * operator-entered start_offset (where ball 1.1 begins).
 *
 * Phase 2 button (Re-align with OCR) is wired here but its backend
 * endpoint may not be live yet — graceful fail handled.
 */
// 2026-06-09 — verbose error surfacer so "Request failed with status
// code 400" stops being the entire error message. We attach phase +
// status code + the body / message we got back, including:
//   - DRF body.detail when our backend returned JSON
//   - DRF body field-validation errors (e.g. video_source_id: [...])
//   - Raw S3 XML error body when the presigned PUT failed
//   - Network errors (timeouts, CORS, DNS) via e.code / e.message
function _detailedError(phase, e) {
  if (e?.response) {
    const code = e.response.status;
    const data = e.response.data;
    // S3 returns XML; parse out the <Code>/<Message> if visible.
    if (typeof data === "string" && data.includes("<Code>")) {
      const code_m = /<Code>([^<]+)<\/Code>/.exec(data);
      const msg_m  = /<Message>([^<]+)<\/Message>/.exec(data);
      return `[${phase}] S3 ${code}: ${(code_m && code_m[1]) || "?"} — ${(msg_m && msg_m[1]) || data.slice(0, 200)}`;
    }
    if (data && typeof data === "object") {
      if (data.detail) return `[${phase}] HTTP ${code}: ${data.detail}`;
      // DRF field-validation shape: { field: [errors] }
      const parts = [];
      for (const [k, v] of Object.entries(data)) {
        parts.push(`${k}: ${Array.isArray(v) ? v.join(", ") : v}`);
      }
      if (parts.length) return `[${phase}] HTTP ${code}: ${parts.join("; ")}`;
    }
    return `[${phase}] HTTP ${code}: ${(typeof data === "string" ? data.slice(0, 200) : "no body")}`;
  }
  return `[${phase}] ${e?.code || ""} ${e?.message || String(e)}`.trim();
}

function WholeMatchUploadPanel({ matchId, disabled, onAfterApply, setUploading, setUploadProgress }) {
  const fileRef = useRef(null);
  const [s3Key, setS3Key] = useState(null);           // populated after PUT succeeds
  const [filename, setFilename] = useState("");
  const [sizeBytes, setSizeBytes] = useState(0);
  const [uploadedAt, setUploadedAt] = useState(null);
  const [slicerStatus, setSlicerStatus] = useState(null);  // {balls_total, clips_in_s3, pct_complete, ...}
  // 2026-08-03 storage policy — masters live in Glacier Deep Archive.
  // videoState: "available" | "archived" | "retrieving" | "restored"
  // (from GET /whole-match-video/). Archived masters need the Retrieve
  // button (Bulk restore, up to 48 h) before playback/tagging works.
  const [videoState, setVideoState] = useState(null);
  const [restoreExpiry, setRestoreExpiry] = useState(null);
  const [startOffsetStr, setStartOffsetStr] = useState("00:00");  // mm:ss when 1.1 starts
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState(null);  // "upload" | "preview" | "apply" | "ocr" | "remove" | "refresh"
  const [result, setResult] = useState(null);          // alignment result
  const [error, setError] = useState(null);
  // {pct, phase_label, done, total} from the OCR job's progress field.
  // Refreshed every poll tick (~3s) while OCR is running.
  const [ocrProgress, setOcrProgress] = useState(null);

  // Rehydrate from S3 on mount + after upload/apply so a page refresh
  // doesn't lose the multi-GB master. Also surfaces slicer progress so
  // the operator can verify per-ball clips are landing.
  const refreshState = async ({ silent = false } = {}) => {
    if (!silent) { setBusy(true); setBusyAction("refresh"); }
    try {
      const r = await api.get(`/api/admin/v2/matches/${matchId}/whole-match-video/`);
      const d = r.data || {};
      if (d.present && d.master) {
        setS3Key(d.master.s3_key);
        setFilename(d.master.filename || "(master)");
        setSizeBytes(d.master.size_bytes || 0);
        setUploadedAt(d.master.uploaded_at || null);
        setVideoState(d.master.video_state || "available");
        setRestoreExpiry(d.master.restore_expiry || null);
      } else {
        setS3Key(null); setFilename(""); setSizeBytes(0); setUploadedAt(null);
        setVideoState(null); setRestoreExpiry(null);
      }
      setSlicerStatus(d.slicer || null);
    } catch (e) {
      if (!silent) setError(_detailedError("refresh", e));
    } finally {
      if (!silent) { setBusy(false); setBusyAction(null); }
    }
  };

  useEffect(() => {
    refreshState({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // While slicing is in progress (clips still landing), poll every 3 s
  // so the operator sees the count climb without manually refreshing.
  useEffect(() => {
    if (!s3Key || !slicerStatus) return;
    const { clips_in_s3 = 0, balls_total = 0 } = slicerStatus;
    if (balls_total === 0) return;
    if (clips_in_s3 >= balls_total) return;
    const t = setInterval(() => refreshState({ silent: true }), 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s3Key, slicerStatus?.clips_in_s3, slicerStatus?.balls_total]);

  const removeVideo = async () => {
    if (!s3Key) return;
    if (!window.confirm(
      "Delete the uploaded full-match video AND every per-ball clip for this match?\n\n" +
      "This drops the master MP4, all sliced per-ball MP4s, and every BallVideo row. " +
      "You'll need to re-upload to re-align."
    )) return;
    setBusy(true); setBusyAction("remove"); setError(null); setResult(null);
    try {
      const r = await api.delete(`/api/admin/v2/matches/${matchId}/whole-match-video/`);
      setResult({
        removed: true,
        deleted_master_objects: r.data?.deleted_master_objects ?? 0,
        deleted_clip_objects: r.data?.deleted_clip_objects ?? 0,
        deleted_ballvideo_rows: r.data?.deleted_ballvideo_rows ?? 0,
      });
      setS3Key(null); setFilename(""); setSizeBytes(0); setUploadedAt(null);
      setSlicerStatus(null);
      await onAfterApply?.();
    } catch (e) {
      setError(_detailedError("remove", e));
    } finally {
      setBusy(false); setBusyAction(null);
    }
  };

  // Kick off the Glacier Deep Archive restore for this match's master.
  // Idempotent on the backend — safe to click again to re-check.
  const retrieveVideo = async () => {
    setBusy(true); setBusyAction("retrieve"); setError(null);
    try {
      const r = await api.post(`/api/admin/v2/matches/${matchId}/retrieve-whole-match-video/`);
      const st = r.data?.state || "retrieving";
      setVideoState(st);
      setRestoreExpiry(r.data?.restore_expiry || null);
    } catch (e) {
      setError(_detailedError("retrieve", e));
    } finally {
      setBusy(false); setBusyAction(null);
    }
  };

  const uploadVideo = async (file) => {
    setBusy(true); setBusyAction("upload"); setError(null); setResult(null);
    setUploading(true);
    setUploadProgress({
      loaded: 0, total: file.size, pct: 0, phase: "uploading",
      innings: null, filenames: [file.name], fileCount: 1, startedAt: Date.now(),
    });
    let phase = "start-multipart";
    let mp = null;   // {upload_id, s3_key, s3_key_within, part_size, part_urls}
    try {
      // 1) Ask the backend to create the multipart upload + hand back
      //    one presigned PUT URL per part. Replaces the old single-PUT
      //    flow which was capped at 5 GB by S3 EntityTooLarge — full-
      //    match videos are typically 10+ GB.
      const startRes = await api.post(
        `/api/admin/v2/matches/${matchId}/start-multipart-upload-whole-match/`,
        { filename: file.name, total_size: file.size },
      );
      mp = startRes.data;
      const { upload_id, s3_key, s3_key_within, part_size, part_urls } = mp;

      // 2) PUT each part. Capture the ETag from the response — S3 needs
      //    it back in CompleteMultipartUpload. CORS bucket policy must
      //    expose the etag header for the browser to read it (zcd-
      //    recordings was set up with ExposeHeaders: ['ETag'] for this).
      phase = "s3-part-put";
      const parts = [];
      let uploadedBeforeThisPart = 0;
      for (const { part_number, url } of part_urls) {
        const start = (part_number - 1) * part_size;
        const end   = Math.min(start + part_size, file.size);
        const blob  = file.slice(start, end);
        const r = await axiosRawPut(url, blob, {
          onUploadProgress: (ev) => {
            const partLoaded = ev.loaded || 0;
            const totalLoaded = uploadedBeforeThisPart + partLoaded;
            const pct = file.size
              ? Math.min(100, Math.round((totalLoaded / file.size) * 100))
              : 0;
            setUploadProgress((p) => p && {
              ...p, loaded: totalLoaded, total: file.size, pct,
              phase: pct >= 100 ? "processing" : "uploading",
            });
          },
        });
        const etag =
          r.headers?.etag || r.headers?.ETag || r.headers?.["etag"] || "";
        if (!etag) {
          throw new Error(
            `S3 part ${part_number} response did not expose an ETag header. ` +
            `Bucket CORS must include ExposeHeaders: ['ETag'].`,
          );
        }
        parts.push({ PartNumber: part_number, ETag: etag });
        uploadedBeforeThisPart = end;
      }

      // 3) Complete — hand the parts list back to the server which
      //    finalises the assembled S3 object.
      phase = "complete-multipart";
      await api.post(
        `/api/admin/v2/matches/${matchId}/complete-multipart-upload-whole-match/`,
        { upload_id, s3_key_within, parts },
      );

      setS3Key(s3_key);
      setFilename(file.name);
      setSizeBytes(file.size);
      setUploadedAt(new Date().toISOString());
      // Pull authoritative state (incl. slicer counters) from the
      // backend so the panel matches what the server sees.
      refreshState({ silent: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("whole-match multipart upload failed:", phase, e, e?.response);
      setError(_detailedError(phase, e));
      // Best-effort abort so we don't leak unfinished multipart uploads
      // (S3 keeps them around indefinitely + charges storage).
      if (mp?.upload_id && mp?.s3_key_within) {
        try {
          await api.post(
            `/api/admin/v2/matches/${matchId}/abort-multipart-upload-whole-match/`,
            { upload_id: mp.upload_id, s3_key_within: mp.s3_key_within },
          );
        } catch { /* ignore abort failure — was best-effort cleanup */ }
      }
    } finally {
      setBusy(false); setBusyAction(null);
      setUploading(false);
      setUploadProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const align = async ({ dryRun }) => {
    if (!s3Key) { setError("Upload the match video first."); return; }
    const startMs = parseHmsToMs(startOffsetStr);
    if (!Number.isFinite(startMs) || startMs < 0) {
      setError("Start offset must be in mm:ss or hh:mm:ss format.");
      return;
    }
    setBusy(true); setBusyAction(dryRun ? "preview" : "apply"); setError(null);
    try {
      const body = {
        video_source_id: s3Key,
        start_offset_ms: startMs,
        dry_run: !!dryRun,
      };
      // Per-ball clip windows are now event-aware (dot/four/six/wicket etc.).
      // No seconds_per_ball / break knobs sent — backend ignores them anyway.
      const r = await api.post(
        `/api/admin/v2/matches/${matchId}/align-full-video/`,
        body,
        { timeout: 5 * 60 * 1000 },
      );
      setResult(r.data);
      if (!dryRun) {
        // Slicer fires in a background thread on the server; pull the
        // first progress snapshot now and the auto-poll loop will keep
        // updating it as clips land.
        refreshState({ silent: true });
        await onAfterApply?.();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("align-full-video failed:", e, e?.response);
      setError(_detailedError(dryRun ? "preview" : "apply", e));
    } finally {
      setBusy(false); setBusyAction(null);
    }
  };

  const runOcr = async () => {
    if (!s3Key) { setError("Upload the match video first."); return; }
    setBusy(true); setBusyAction("ocr"); setError(null);
    setOcrProgress({ pct: 0, phase_label: "Starting…", done: 0, total: 0 });
    try {
      // Pass the start_offset_ms field the operator filled in too —
      // OCR uses it as the seed for the math-extrapolation fallback
      // (balls the scoreboard OCR couldn't read). When OCR detects
      // every ball it's irrelevant; when OCR misses some, the seed
      // anchors the fallback windows at the right wall-clock spot
      // instead of the broadcast opening.
      const startMs = parseHmsToMs(startOffsetStr);
      const r = await api.post(
        `/api/admin/v2/matches/${matchId}/align-with-ocr/`,
        {
          video_source_id: s3Key,
          start_offset_ms: Number.isFinite(startMs) && startMs >= 0 ? startMs : 0,
        },
      );
      const { job_id } = r.data;
      // Poll every 2 s. OCR's longest phase (frame OCR) emits progress
      // every 50 frames so a 2 s poll catches most updates without
      // hammering the API.
      const startedAt = Date.now();
      let finalJob = null;
      while (Date.now() - startedAt < 60 * 60 * 1000) {  // up to 60 min for long videos
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const s = await api.get(`/api/admin/v2/upload-jobs/${job_id}/`);
          if (s.data?.progress) {
            setOcrProgress({
              pct: s.data.progress.pct ?? 0,
              phase_label: s.data.progress.phase_label || s.data.progress.phase || "Working…",
              done: s.data.progress.done ?? 0,
              total: s.data.progress.total ?? 0,
            });
          }
          if (s.data.status === "complete" || s.data.status === "failed") {
            finalJob = s.data; break;
          }
        } catch (_) {}
      }
      if (!finalJob) throw new Error("OCR job timed out.");
      if (finalJob.status === "failed") {
        throw new Error(`OCR failed: ${finalJob.error || "unknown"}`);
      }
      setOcrProgress({ pct: 100, phase_label: "Done", done: 1, total: 1 });
      setResult(finalJob.result || finalJob);
      // OCR aligner now fires the per-ball Lambda slicer too —
      // pull slicer counters so the panel shows clips-in-S3 progress.
      refreshState({ silent: true });
      await onAfterApply?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("align-with-ocr failed:", e, e?.response);
      if (e?.response?.status === 404) {
        setError("OCR aligner not deployed yet. Use the math-only fallback button.");
      } else {
        setError(_detailedError("ocr", e));
      }
    } finally {
      setBusy(false); setBusyAction(null);
      // Keep the bar visible for 3 s after completion so the operator
      // sees the final state; then clear it.
      setTimeout(() => setOcrProgress(null), 3000);
    }
  };

  return (
    <div className="mt-4 border border-border-default rounded-lg p-4 bg-bg-secondary/20">
      <div className="flex items-center gap-2 mb-1">
        <Video className="w-4 h-4 text-text-secondary" />
        <h3 className="text-sm font-semibold">Upload whole-match video</h3>
      </div>
      <p className="text-[11px] text-text-secondary mb-3">
        One single MP4/MOV that covers the whole match. The math aligner computes per-ball{" "}
        timestamps from the format default (~25 s/ball on T20) and the start offset you provide.{" "}
        Run a preview first to verify, then apply. Use "Re-align with OCR" to refine using the
        burned-in scoreboard overlay.
      </p>

      {/* Step 1: file picker — persisted across page refreshes via
          the GET /whole-match-video/ rehydrate call on mount. */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start mb-3">
        <div className="text-xs">
          {s3Key ? (
            <div className="space-y-1">
              <div className="text-emerald-300">
                ✓ Uploaded: <span className="font-mono">{filename}</span>
              </div>
              <div className="text-[10px] text-text-secondary font-mono">
                {sizeBytes ? `${(sizeBytes / 1024 / 1024 / 1024).toFixed(2)} GB` : "—"}
                {uploadedAt ? ` · ${new Date(uploadedAt).toLocaleString()}` : ""}
              </div>
              {/* 2026-08-03 — archive tier status. Masters move to Glacier
                  Deep Archive on the day after upload; tagging later needs
                  a Retrieve first (Bulk restore, up to 48 h). */}
              {videoState === "archived" && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30">
                    In Deep Archive — retrieve before tagging
                  </span>
                  <button
                    onClick={retrieveVideo}
                    disabled={busy}
                    className="text-[10px] px-2 py-1 rounded bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-40"
                    title="Start a Bulk restore from Glacier Deep Archive. The video becomes playable/taggable within 48 hours and stays available for 14 days."
                  >
                    {busyAction === "retrieve" ? "Requesting…" : "Retrieve video (ready in ≤48 h)"}
                  </button>
                </div>
              )}
              {videoState === "retrieving" && (
                <div className="text-[10px] mt-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-block">
                  Retrieving from archive… up to 48 h from the request. Check back later (Refresh).
                </div>
              )}
              {videoState === "restored" && (
                <div className="text-[10px] mt-1 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 inline-block">
                  Retrieved from archive — taggable
                  {restoreExpiry ? ` until ${new Date(restoreExpiry).toLocaleDateString()}` : ""}.
                </div>
              )}
              {slicerStatus && slicerStatus.balls_total > 0 && (
                <div className="text-[11px] mt-1">
                  <span className={
                    slicerStatus.clips_in_s3 >= slicerStatus.balls_total
                      ? "text-emerald-300" : "text-amber-300"
                  }>
                    Slicer: {slicerStatus.clips_in_s3}/{slicerStatus.balls_total} clips in S3 ({slicerStatus.pct_complete}%)
                  </span>
                  {slicerStatus.clips_in_s3 < slicerStatus.balls_total && (
                    <span className="text-text-secondary ml-1.5">— polling every 3 s…</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <span className="text-text-secondary">No video uploaded yet</span>
          )}
        </div>
        <div className="flex flex-col gap-1.5 items-stretch md:items-end">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={disabled || busy}
            className="bg-button-primary hover:brightness-110 disabled:opacity-50 px-3 py-1.5 rounded text-xs font-semibold text-white inline-flex items-center justify-center gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" />
            {busyAction === "upload" ? "Uploading…" : s3Key ? "Replace video" : "Choose match video"}
          </button>
          {s3Key && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => refreshState()}
                disabled={busy}
                className="text-[10px] px-2 py-1 rounded border border-border-default hover:bg-bg-tertiary disabled:opacity-40 inline-flex items-center gap-1"
                title="Recheck S3 for the master file + per-ball clip count"
              >
                <RefreshCw className="w-3 h-3" />
                {busyAction === "refresh" ? "…" : "Refresh"}
              </button>
              <button
                onClick={removeVideo}
                disabled={busy}
                className="text-[10px] px-2 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 disabled:opacity-40 inline-flex items-center gap-1"
                title="Delete the master MP4, every per-ball clip, and every BallVideo row for this match"
              >
                <Trash2 className="w-3 h-3" />
                {busyAction === "remove" ? "Removing…" : "Remove video"}
              </button>
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="video/*,.mp4,.mov,.m4v,.mkv,.webm"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadVideo(f);
          }}
          className="hidden"
        />
      </div>

      {/* Step 2: start offset + advanced overrides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="text-xs">
          <span className="block mb-0.5 text-text-secondary">
            Start offset (mm:ss when ball <span className="font-mono">1.1</span> bowled)
          </span>
          <input
            type="text"
            value={startOffsetStr}
            onChange={(e) => setStartOffsetStr(e.target.value)}
            placeholder="00:00"
            className="w-full px-2 py-1 bg-bg-tertiary border border-border-default rounded font-mono"
          />
        </label>
        <div className="text-[11px] text-text-secondary leading-relaxed">
          Clip length is decided per ball:{" "}
          <span className="font-mono">dot ~10s</span>{" "}
          ·{" "}
          <span className="font-mono">four ~22s</span>{" "}
          ·{" "}
          <span className="font-mono">six ~26s</span>{" "}
          ·{" "}
          <span className="font-mono">wicket ~32s</span>.{" "}
          Boundary replays, wicket celebrations, and end-of-over
          packages are captured automatically — no manual timing knobs.
        </div>
      </div>

      {/* Step 3: actions
           2026-06-09 — "Apply alignment" now runs OCR-first with
           math-extrapolation fallback per ball, so each clip's
           length matches the actual on-screen scoreboard window
           instead of a fixed 25 s default that truncates long
           balls. "Math only (fast)" stays as the legacy escape
           hatch when OCR isn't viable. */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => align({ dryRun: true })}
          disabled={disabled || busy || !s3Key}
          className="text-xs px-2.5 py-1.5 rounded border border-border-default hover:bg-bg-tertiary disabled:opacity-40"
          title="Preview the math-extrapolation timestamps — no DB writes, no slicing"
        >
          {busyAction === "preview" ? "Computing…" : "Preview (math)"}
        </button>
        <button
          onClick={runOcr}
          disabled={disabled || busy || !s3Key}
          className="text-xs px-2.5 py-1.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-40"
          title="OCR the scoreboard to derive per-ball clip windows; balls the OCR misses fall back to the format default timer"
        >
          {busyAction === "ocr" ? "Running OCR…" : "Apply alignment (OCR + math fallback)"}
        </button>
        <span className="flex-1" />
        <button
          onClick={() => align({ dryRun: false })}
          disabled={disabled || busy || !s3Key}
          className="text-xs px-2.5 py-1.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-40"
          title="Skip OCR — use the format default (T20=25s, ODI=30s) for every ball. Faster but truncates long deliveries."
        >
          {busyAction === "apply" ? "Applying…" : "Math only (fast)"}
        </button>
      </div>

      {ocrProgress && busyAction === "ocr" && (
        <div className="mt-3 p-3 border border-emerald-500/30 rounded bg-emerald-500/5">
          <div className="flex items-center justify-between text-[11px] mb-1.5">
            <span className="text-emerald-200 font-medium">{ocrProgress.phase_label}</span>
            <span className="text-emerald-300 font-mono tabular-nums">
              {ocrProgress.pct}%
              {ocrProgress.total > 1 && ocrProgress.done > 0 && (
                <span className="text-text-secondary ml-1.5">
                  ({ocrProgress.done.toLocaleString()}/{ocrProgress.total.toLocaleString()})
                </span>
              )}
            </span>
          </div>
          <div className="w-full h-1.5 bg-bg-tertiary rounded overflow-hidden">
            <div
              className="h-full bg-emerald-400 transition-all duration-300 ease-out"
              style={{ width: `${Math.max(2, Math.min(100, ocrProgress.pct))}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs text-red-300 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {result?.removed && (
        <div className="mt-3 p-3 border border-red-500/40 rounded bg-red-500/10 text-xs space-y-1">
          <div className="text-red-200">
            ✓ Removed master + per-ball clips ·{" "}
            <span className="font-mono">
              {result.deleted_master_objects} master · {result.deleted_clip_objects} clips · {result.deleted_ballvideo_rows} rows
            </span>
          </div>
        </div>
      )}

      {result && !result.removed && (
        <div className="mt-3 p-3 border border-border-default rounded bg-bg-tertiary/30 text-xs space-y-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-emerald-300">
              ✓ {result.balls_aligned} balls aligned
            </span>
            {typeof result.ocr_mapped === "number" && (
              <span className="text-emerald-300/80">
                · OCR mapped {result.ocr_mapped}
              </span>
            )}
            {typeof result.interpolated === "number" && result.interpolated > 0 && (
              <span className="text-blue-300/80">
                · interpolated {result.interpolated}
              </span>
            )}
            {typeof result.math_fallback === "number" && result.math_fallback > 0 && (
              <span className="text-amber-300">
                · math fallback {result.math_fallback}
              </span>
            )}
            {result.roi_name && (
              <span className="text-text-secondary">
                · ROI: <span className="font-mono">{result.roi_name}</span>
                {typeof result.roi_hit_rate === "number" && (
                  <span> ({Math.round(result.roi_hit_rate * 100)}% hit)</span>
                )}
              </span>
            )}
            {typeof result.transitions_detected === "number" && (
              <span className="text-text-secondary">
                · {result.transitions_detected} transitions
              </span>
            )}
            {result.rows_written > 0 && (
              <span className="text-blue-300">
                ↻ {result.rows_written} BallVideo rows written
              </span>
            )}
            {result.dry_run && (
              <span className="text-amber-300">dry-run — not saved</span>
            )}
          </div>
          {result.event_breakdown && Object.keys(result.event_breakdown).length > 0 && (
            <div className="text-[11px] text-text-secondary font-mono flex flex-wrap gap-x-3 gap-y-0.5">
              {["dot","single","double","three","four","six","wicket","default"]
                .filter(k => result.event_breakdown[k])
                .map(k => (
                  <span key={k}>{k}: <span className="text-text-primary">{result.event_breakdown[k]}</span></span>
                ))}
            </div>
          )}
          {result.warnings?.length > 0 && (
            <ul className="text-amber-300 list-disc ml-4">
              {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
          {result.preview?.length > 0 && (
            <details>
              <summary className="cursor-pointer text-text-secondary">
                First {result.preview.length} ball timestamps
              </summary>
              <table className="mt-1 font-mono text-[10px] w-full">
                <thead className="text-text-secondary">
                  <tr><th className="text-left">over.ball</th><th className="text-left">inn</th><th className="text-left">start</th></tr>
                </thead>
                <tbody>
                  {result.preview.map((p, i) => (
                    <tr key={i}>
                      <td>{p.over_ball}</td>
                      <td>{p.innings}</td>
                      <td>{p.start_hms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Inline file picker on a single missing-clip row. The parent's
 * uploadClipForBall handler renames the file to <over>.<ball>.<ext>
 * + posts with innings + convention=scorecard so the binder lands on
 * this exact Ball.
 */
function MissingClipUploader({ row, busy, onPick }) {
  const ref = useRef(null);
  return (
    <>
      <button
        onClick={() => !busy && ref.current?.click()}
        disabled={busy}
        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 disabled:opacity-40"
        title={
          `Upload the missing clip for innings ${row.innings} `
          + `${row.over}.${row.ball} — pinned to this exact ball`
        }
      >
        <Upload className="w-3 h-3" />
        {busy ? "uploading…" : "upload clip"}
      </button>
      <input
        ref={ref}
        type="file"
        accept="video/*,.mp4,.mov,.m4v,.mkv,.webm"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
        className="hidden"
      />
    </>
  );
}
